import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

const STORAGE_KEY = 'stikes-theme';
const VALID = ['system', 'light', 'dark'];

// Baca preferensi tersimpan; default 'system' bila belum ada / tidak valid
const getStoredTheme = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return VALID.includes(saved) ? saved : 'system';
  } catch {
    return 'system';
  }
};

const systemPrefersDark = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

// Terapkan class .dark + color-scheme ke <html>
const applyTheme = (isDark) => {
  const root = document.documentElement;
  root.classList.toggle('dark', isDark);
  root.style.colorScheme = isDark ? 'dark' : 'light';
};

export const ThemeProvider = ({ children }) => {
  // Mode pilihan user: 'system' | 'light' | 'dark'
  const [theme, setThemeState] = useState(getStoredTheme);
  // Tema efektif yang benar-benar aktif
  const [isDark, setIsDark] = useState(() =>
    getStoredTheme() === 'dark' ||
    (getStoredTheme() === 'system' && systemPrefersDark())
  );

  // Hitung ulang tema efektif setiap kali mode berubah, sinkronkan ke <html> & storage
  useEffect(() => {
    const resolved = theme === 'system' ? systemPrefersDark() : theme === 'dark';
    setIsDark(resolved);
    applyTheme(resolved);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* storage tidak tersedia — abaikan */
    }
  }, [theme]);

  // Ikuti perubahan tema OS secara live saat mode = 'system'
  useEffect(() => {
    if (theme !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => {
      setIsDark(e.matches);
      applyTheme(e.matches);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (VALID.includes(next)) setThemeState(next);
  }, []);

  // Kompatibilitas mundur: toggle sederhana antara terang & gelap eksplisit
  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const currentlyDark = prev === 'dark' || (prev === 'system' && systemPrefersDark());
      return currentlyDark ? 'light' : 'dark';
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
